import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import InstallerProjectsList from '@/components/InstallerProjectsList'
import { useProjectsInstaller, InstallerProject } from '@/hooks/useProjectsInstaller'
import { useTranslation } from '@/hooks/useTranslation'

interface InstallerProjectsSectionProps {
  /** Max number of projects rendered inline; the rest is reachable from the "see all" link. */
  limit?: number
}

/**
 * Shortcut shown on the home page so an authenticated installer can pick
 * the project he has to work on without going through the dashboard.
 */
const InstallerProjectsSection: FC<InstallerProjectsSectionProps> = ({ limit = 4 }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentProjects, loading, error } = useProjectsInstaller()

  const handleSelect = (project: InstallerProject) => {
    if (project.project?.id) navigate(`/project/${project.project.id}`)
  }

  // Stay silent on error: the home page must remain usable even if the API is down.
  if (error) return null

  return (
    <div className='mb-8'>
      <div className='flex items-center justify-between mb-4'>
        <h2 className='text-2xl font-semibold'>{t('MY_PROJECTS_TITLE')}</h2>
        {currentProjects.length > limit && (
          <button
            className='text-sm font-medium text-[var(--brand-blue)] hover:underline'
            onClick={() => navigate('/my-projects')}
          >
            {t('MY_PROJECTS_SEE_ALL')}
          </button>
        )}
      </div>

      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <span className='spinner' style={{ borderTopColor: 'var(--brand-red)' }} />
        </div>
      ) : (
        <InstallerProjectsList projects={currentProjects.slice(0, limit)} onSelect={handleSelect} />
      )}
    </div>
  )
}

export default InstallerProjectsSection
